import { Suspense, lazy, useCallback, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import { createPortal } from "react-dom";
import { shopModeApi, type FloorBox, type FloorScannedItem } from "../../api/shopMode";
import { useT } from "../../i18n/LanguageProvider";
import { money } from "../../i18n/intl";

const BarcodeScanner = lazy(() =>
  import("../BarcodeScanner").then((m) => ({ default: m.BarcodeScanner })),
);

function hasStatus(err: unknown): err is { status: number } {
  return (
    typeof err === "object" &&
    err !== null &&
    "status" in err &&
    typeof (err as { status: unknown }).status === "number"
  );
}

type Step = "scanning" | "looking-up" | "choose" | "notfound" | "error";

/**
 * G-73: scan a barcode, end up holding one item.
 *
 * The camera, the lookup, and — because of G-61, where one EAN legitimately
 * names several items (two Ming candle scents share a code and differ only by
 * SKU) — the "which one is in your hand?" question when more than one matches.
 * Nothing about what happens NEXT: the caller decides that, which is what
 * lets the stock list open an item card here and the till open a sale.
 *
 * Portalled to the body for the same reason the item card is: the stock
 * panel it opens from carries a `backdrop-filter`, which would otherwise trap
 * this full-screen camera inside a 200-pixel-wide box.
 *
 * The one copy of these three steps ("scanning" → "looking-up" → "choose").
 * An older scan-first kiosk, `ShopFloorScanFlow.tsx`, carried its own copy;
 * nothing mounted it, and D-73 (G-121 S3) deleted it.
 */
export interface ShopFloorBarcodeResolverProps {
  /** The florist picked an item. */
  onResolved(item: FloorScannedItem): void;
  /**
   * G-84: the scanned label was a storage box's. A code shaped `BX000017`
   * is tried as a box FIRST and as an item only when no box in this shop
   * carries it; callers that do not care about boxes leave this out and
   * see the old behaviour exactly.
   */
  onBoxResolved?(box: FloorBox): void;
  /** Backed out, or gave up on an unknown code. */
  onCancel(): void;
}

/** A box barcode is `BX` + digits; an item barcode is digits alone (G-61). */
export function looksLikeBoxBarcode(value: string): boolean {
  return /^BX\d{1,6}$/i.test(value.trim());
}

export function ShopFloorBarcodeResolver({
  onResolved,
  onBoxResolved,
  onCancel,
}: ShopFloorBarcodeResolverProps) {
  const { t } = useT();
  const [step, setStep] = useState<Step>("scanning");
  const [matches, setMatches] = useState<FloorScannedItem[]>([]);
  const [scanned, setScanned] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const restart = useCallback(() => {
    setMatches([]);
    setScanned(null);
    setErrorMsg(null);
    setStep("scanning");
  }, []);

  const handleDetected = useCallback(
    (value: string) => {
      const barcode = value.trim();
      if (!barcode) return;
      setScanned(barcode);
      setStep("looking-up");
      void (async () => {
        try {
          if (onBoxResolved && looksLikeBoxBarcode(barcode)) {
            try {
              onBoxResolved(await shopModeApi.floorFindBoxByBarcode(barcode));
              return;
            } catch (err: unknown) {
              // Not a box in this shop: fall through to the item lookup, so a
              // code that happens to start with BX still finds its item.
              if (!(hasStatus(err) && err.status === 404)) throw err;
            }
          }
          const found = await shopModeApi.floorFindItemsByBarcode(barcode);
          if (found.length > 1) {
            setMatches(found);
            setStep("choose");
            return;
          }
          onResolved(found[0]);
        } catch (err: unknown) {
          if (hasStatus(err) && err.status === 404) {
            setStep("notfound");
            return;
          }
          setErrorMsg(errorText(err, "resolver.lookupFailed"));
          setStep("error");
        }
      })();
    },
    [onResolved, onBoxResolved],
  );

  if (step === "scanning") {
    return createPortal(
      <Suspense
        fallback={
          <div className="shop-floor-scan-flow empty-state">{t("itemCard.startingCamera")}</div>
        }
      >
        <BarcodeScanner onDetected={handleDetected} onClose={onCancel} />
      </Suspense>,
      document.body,
    );
  }

  if (step === "looking-up") {
    return createPortal(
      <div className="shop-floor-scan-flow sf-scan-overlay">
        <div className="empty-state">{t("resolver.lookingUp", { code: scanned ?? "" })}</div>
      </div>,
      document.body,
    );
  }

  if (step === "choose") {
    return createPortal(
      <div className="shop-floor-scan-flow sf-scan-overlay">
        <div className="shop-floor-scan-card">
          <p className="shop-floor-scan-title">{t("resolver.whichOne")}</p>
          <p className="muted">
            {t("resolver.shareBarcode", {
              count: matches.length,
              code: scanned ?? "",
            })}
          </p>
          <div className="shop-floor-action-stack">
            {matches.map((candidate) => (
              <button
                key={candidate.id}
                type="button"
                className="button shop-floor-action-btn"
                onClick={() => onResolved(candidate)}
              >
                {candidate.name}
                <span className="muted" style={{ display: "block", fontSize: 14 }}>
                  {candidate.category.label}
                  {candidate.sku ? ` · ${candidate.sku}` : ""}
                  {candidate.saleUnitPriceEur
                    ? ` · ${money(candidate.saleUnitPriceEur)}`
                    : ""}
                </span>
              </button>
            ))}
            <button type="button" className="button plain" onClick={restart}>
              {t("resolver.scanAgain")}
            </button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <div className="shop-floor-scan-flow sf-scan-overlay">
      <div className="shop-floor-scan-card">
        <p className="shop-floor-scan-title">
          {step === "notfound"
            ? t("finder.barcodeUnknown")
            : t("resolver.somethingWrong")}
        </p>
        {step === "notfound" && scanned ? (
          <p className="muted">{t("resolver.scannedCode", { code: scanned })}</p>
        ) : null}
        {errorMsg ? <div className="alert error">{errorMsg}</div> : null}
        <div className="shop-floor-action-stack">
          <button
            type="button"
            className="button shop-floor-action-btn"
            onClick={restart}
          >
            {t("resolver.scanAgain")}
          </button>
          <button type="button" className="button plain" onClick={onCancel}>
            {t("resolver.done")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
